import { generateClient } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import awsconfig from "../aws-exports";

let publicClient;

function getPublicClient() {
  if (!publicClient) {
    publicClient = generateClient();
  }
  return publicClient;
}

export async function graphqlWithAdminToken(query, variables) {
  const session = await fetchAuthSession();
  const token =
    session.tokens?.idToken?.toString() || session.tokens?.accessToken?.toString();

  if (!token) {
    throw new Error("Geen geldige Cognito sessie gevonden voor adminactie.");
  }

  const response = await fetch(awsconfig.aws_appsync_graphqlEndpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
    body: JSON.stringify({ query, variables }),
  });

  const json = await response.json();
  if (json.errors?.length) {
    throw new Error(json.errors.map((error) => error.message).join("; "));
  }

  return json;
}

export const listPageContentsQuery = /* GraphQL */ `
  query ListPageContents($filter: ModelPageContentFilterInput, $limit: Int, $nextToken: String) {
    listPageContents(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        pageName
        sectionName
        contentType
        content
        order
        isActive
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const createPageContentMutation = /* GraphQL */ `
  mutation CreatePageContent($input: CreatePageContentInput!) {
    createPageContent(input: $input) {
      id
      pageName
      sectionName
      contentType
      content
      order
      isActive
      createdAt
      updatedAt
    }
  }
`;

export const updatePageContentMutation = /* GraphQL */ `
  mutation UpdatePageContent($input: UpdatePageContentInput!) {
    updatePageContent(input: $input) {
      id
      pageName
      sectionName
      contentType
      content
      order
      isActive
      createdAt
      updatedAt
    }
  }
`;

export const deletePageContentMutation = /* GraphQL */ `
  mutation DeletePageContent($input: DeletePageContentInput!) {
    deletePageContent(input: $input) {
      id
    }
  }
`;

export const listMediaAssetsQuery = /* GraphQL */ `
  query ListMediaAssets($filter: ModelMediaAssetFilterInput, $limit: Int, $nextToken: String) {
    listMediaAssets(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        altText
        caption
        description
        bucket
        key
        path
        url
        contentType
        size
        tags
        sourceUrl
        uploadedBy
        isArchived
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const createMediaAssetMutation = /* GraphQL */ `
  mutation CreateMediaAsset($input: CreateMediaAssetInput!) {
    createMediaAsset(input: $input) {
      id
      title
      key
      path
      contentType
      size
      createdAt
      updatedAt
    }
  }
`;

export const updateMediaAssetMutation = /* GraphQL */ `
  mutation UpdateMediaAsset($input: UpdateMediaAssetInput!) {
    updateMediaAsset(input: $input) {
      id
      title
      altText
      caption
      description
      tags
      isArchived
      updatedAt
    }
  }
`;

export async function listPageContents(pageName) {
  const response = await graphqlWithAdminToken(listPageContentsQuery, {
      filter: pageName ? { pageName: { eq: pageName } } : undefined,
      limit: 100,
  });

  return [...response.data.listPageContents.items].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );
}

export async function listPublicPageContents(pageName) {
  const response = await getPublicClient().graphql({
    query: listPageContentsQuery,
    variables: {
      filter: pageName ? { pageName: { eq: pageName } } : undefined,
      limit: 100,
    },
    authMode: "apiKey",
  });

  return [...response.data.listPageContents.items].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );
}

export const listContentBlocksByPageContentIdQuery = /* GraphQL */ `
  query ContentBlocksByPageContentId(
    $pageContentId: ID!
    $filter: ModelContentBlockFilterInput
    $limit: Int
    $nextToken: String
  ) {
    contentBlocksByPageContentId(
      pageContentId: $pageContentId
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        pageContentId
        blockType
        content
        order
        isActive
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export async function listPublicContentBlocks(pageContentId) {
  const response = await getPublicClient().graphql({
    query: listContentBlocksByPageContentIdQuery,
    variables: {
      pageContentId,
      filter: { isActive: { eq: true } },
      limit: 200,
    },
    authMode: "apiKey",
  });

  return [...response.data.contentBlocksByPageContentId.items].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );
}

export async function savePageContent(input) {
  const content = normalizeJsonContent(input.content);
  const payload = sanitizeMutationInput({
    ...input,
    content,
    order: Number(input.order || 0),
    isActive: Boolean(input.isActive),
  });

  if (payload.id) {
    return graphqlWithAdminToken(updatePageContentMutation, { input: payload });
  }

  return graphqlWithAdminToken(createPageContentMutation, { input: payload });
}

export async function deletePageContent(id) {
  return graphqlWithAdminToken(deletePageContentMutation, { input: { id } });
}

export const deleteContentBlockMutation = /* GraphQL */ `
  mutation DeleteContentBlock($input: DeleteContentBlockInput!) {
    deleteContentBlock(input: $input) {
      id
    }
  }
`;

export const createContentBlockMutation = /* GraphQL */ `
  mutation CreateContentBlock($input: CreateContentBlockInput!) {
    createContentBlock(input: $input) {
      id
      pageContentId
      blockType
      content
      order
      isActive
      createdAt
      updatedAt
    }
  }
`;

export const updateContentBlockMutation = /* GraphQL */ `
  mutation UpdateContentBlock($input: UpdateContentBlockInput!) {
    updateContentBlock(input: $input) {
      id
      pageContentId
      blockType
      content
      order
      isActive
      updatedAt
    }
  }
`;

export async function saveContentBlock(input) {
  const content = normalizeJsonContent(input.content);
  const payload = sanitizeMutationInput({
    ...input,
    content,
    order: Number(input.order || 0),
    isActive: Boolean(input.isActive),
  });

  if (payload.id) {
    return graphqlWithAdminToken(updateContentBlockMutation, { input: payload });
  }

  return graphqlWithAdminToken(createContentBlockMutation, { input: payload });
}

export async function listContentBlocks(pageContentId) {
  const response = await graphqlWithAdminToken(listContentBlocksByPageContentIdQuery, {
      pageContentId,
      limit: 200,
  });

  return [...response.data.contentBlocksByPageContentId.items].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );
}

export async function deleteContentBlock(id) {
  return graphqlWithAdminToken(deleteContentBlockMutation, { input: { id } });
}

export async function listMediaAssets() {
  const response = await graphqlWithAdminToken(listMediaAssetsQuery, {
    filter: { isArchived: { ne: true } },
    limit: 200,
  });

  return [...response.data.listMediaAssets.items].sort((a, b) =>
    (b.createdAt || "").localeCompare(a.createdAt || "")
  );
}

export async function createMediaAsset(input) {
  return graphqlWithAdminToken(createMediaAssetMutation, { input });
}

export async function updateMediaAsset(input) {
  return graphqlWithAdminToken(updateMediaAssetMutation, {
    input: sanitizeMutationInput(input),
  });
}

function normalizeJsonContent(content) {
  if (typeof content !== "string") {
    return JSON.stringify(content ?? {});
  }

  JSON.parse(content);
  return content;
}

function sanitizeMutationInput(input) {
  const {
    createdAt,
    updatedAt,
    __typename,
    _deleted,
    _lastChangedAt,
    _version,
    owner,
    ...payload
  } = input;

  if (!payload.id) {
    delete payload.id;
  }

  return payload;
}
